// Colours for iTorgo's category artwork (CategoryArt / SubcategoryArt).
// One palette per top-level category; sub-categories inherit their parent's
// so a sub-category glyph always reads as part of its family.
export interface ArtPalette { bg: string; accent: string; soft: string; ink: string }

export const CATEGORY_PALETTES: Record<string, ArtPalette> = {
  electronics: { bg: '#E6EDF5', accent: '#9DB8D8', soft: '#F4F8FC', ink: '#1D2B3A' },
  vehicles: { bg: '#F4E7C9', accent: '#E0B24A', soft: '#FBF4E3', ink: '#2A2118' },
  'real-estate': { bg: '#EFE3D6', accent: '#D49A6A', soft: '#F9F2EA', ink: '#3A2518' },
  livestock: { bg: '#E4ECD9', accent: '#FBF8EF', soft: '#F3F7EC', ink: '#26341C' },
  jewelry: { bg: '#F3E1E4', accent: '#B9D6E6', soft: '#FBF1F3', ink: '#3F1D25' },
  art: { bg: '#E9E1F0', accent: '#8C74B8', soft: '#F6F1FA', ink: '#2A1F3A' },
  antiques: { bg: '#E9E1F0', accent: '#8C74B8', soft: '#F6F1FA', ink: '#2A1F3A' },
  machinery: { bg: '#E2EBDD', accent: '#6E9E4F', soft: '#F1F6EE', ink: '#1F2B18' },
  'home-goods': { bg: '#E3ECEA', accent: '#5E8F86', soft: '#F1F6F5', ink: '#1C2E2B' },
}

export const FALLBACK_PALETTE: ArtPalette = { bg: '#ECE8E0', accent: '#C9A24B', soft: '#F7F4EE', ink: '#2A2118' }

// Sub-category slug → parent category slug (see data/categories.ts).
export const SUBCATEGORY_PARENT: Record<string, string> = {
  phones: 'electronics', laptops: 'electronics', appliances: 'electronics',
  sedans: 'vehicles', suvs: 'vehicles', commercial_transport: 'vehicles', moto: 'vehicles',
  commercial: 'real-estate', apartments: 'real-estate', houses: 'real-estate', lands: 'real-estate',
  cows: 'livestock', horses: 'livestock', sheep: 'livestock', poultry: 'livestock', feed: 'livestock',
  gold: 'jewelry', watches: 'jewelry', national: 'jewelry',
  shyrdak: 'antiques', coins: 'antiques', paintings: 'antiques',
  tractors: 'machinery', construction: 'machinery', attachments: 'machinery',
  'living-room': 'home-goods', bedroom: 'home-goods', 'kitchen-dining': 'home-goods', 'home-decor': 'home-goods',
}

// The site palette the art should sit in: its dark ink (outlines, motifs)
// and page ground (tile tints lean toward it). Keeps each category's own hue
// but makes the artwork follow Admin > Design palettes.
export interface ArtTone { ink: string; ground: string }

function mix(hex: string, target: string, amount: number): string {
  const a = hex.replace('#', ''), b = target.replace('#', '')
  if (!/^[0-9a-fA-F]{6}$/.test(a) || !/^[0-9a-fA-F]{6}$/.test(b)) return hex
  const ch = (i: number) => {
    const x = parseInt(a.substring(i, i + 2), 16), y = parseInt(b.substring(i, i + 2), 16)
    return Math.round(x + (y - x) * amount).toString(16).padStart(2, '0')
  }
  return `#${ch(0)}${ch(2)}${ch(4)}`.toUpperCase()
}

export function paletteFor(slug: string, tone?: ArtTone): ArtPalette {
  const base = CATEGORY_PALETTES[slug] || CATEGORY_PALETTES[SUBCATEGORY_PARENT[slug] || ''] || FALLBACK_PALETTE
  if (!tone) return base
  return {
    // Tiles lean well into the page ground so warm hues don't fight a cool
    // palette (and vice versa); the category's own hue still shows through.
    bg: mix(base.bg, tone.ground, 0.55),
    soft: mix(base.soft, tone.ground, 0.45),
    accent: base.accent,
    ink: tone.ink,
  }
}
